interface ConfidenceBadgeProps {
    level: string | null | undefined;
    isEstimated?: boolean;
    showTooltip?: boolean;
    size?: 'sm' | 'md';
}

const CONFIDENCE_CONFIG: Record<string, { bg: string; text: string; label: string; tooltip: string }> = {
    high: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-700 dark:text-green-400',
        label: 'High confidence',
        tooltip: 'Multiple clear satellite images confirmed this assessment',
    },
    medium: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
        text: 'text-yellow-700 dark:text-yellow-400',
        label: 'Medium confidence',
        tooltip: 'Some data limitations but result is reasonable',
    },
    low: {
        bg: 'bg-orange-100 dark:bg-orange-900/30',
        text: 'text-orange-700 dark:text-orange-400',
        label: 'Low confidence',
        tooltip: 'Significant limitations — manual review recommended',
    },
    insufficient_data: {
        bg: 'bg-gray-100 dark:bg-gray-700',
        text: 'text-gray-600 dark:text-gray-400',
        label: 'Insufficient data',
        tooltip: 'Not enough data for reliable assessment',
    },
};

export default function ConfidenceBadge({ level, isEstimated, showTooltip = true, size = 'sm' }: ConfidenceBadgeProps) {
    const config = level ? CONFIDENCE_CONFIG[level] : null;

    if (!config && !isEstimated) return null;

    const sizeClasses = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-xs px-2.5 py-1';

    return (
        <div className="inline-flex items-center gap-1.5">
            {isEstimated && (
                <span
                    className={`inline-flex items-center rounded-full font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 ${sizeClasses}`}
                    title="This result is preliminary/estimated and subject to revision"
                >
                    Estimated
                </span>
            )}
            {config && (
                <span
                    className={`inline-flex items-center rounded-full font-medium ${config.bg} ${config.text} ${sizeClasses}`}
                    title={showTooltip ? config.tooltip : undefined}
                >
                    {config.label}
                </span>
            )}
        </div>
    );
}

export function ConfidenceIndicator({ level, isEstimated }: { level: string | null | undefined; isEstimated?: boolean }) {
    if (!level && !isEstimated) return null;

    const config = level ? CONFIDENCE_CONFIG[level] : null;

    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
            {isEstimated && (
                <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <span className="text-amber-600 text-sm font-medium">Preliminary result</span>
                    <span className="text-xs text-amber-600/80">
                        This result is estimated and subject to revision based on data availability.
                    </span>
                </div>
            )}
            {config && (
                <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Confidence</span>
                    <span className={`inline-flex items-center rounded-full font-medium text-xs px-2.5 py-1 ${config.bg} ${config.text}`}>
                        {config.label}
                    </span>
                </div>
            )}
            {config && (
                <p className="text-xs text-gray-500">{config.tooltip}</p>
            )}
        </div>
    );
}
